import * as THREE from 'three';
import type { FeatureFace } from '../FeatureFace';
import { FeatureFaceSpatialAccelerationEnsurer } from './FeatureFaceSpatialAccelerationEnsurer';
import type { FeatureFaceSpatialHitInfo } from './featureFaceSpatialQueryTypes';

/**
 * 两特征面网格之间的最近点对（世界坐标）。
 */
export interface FeatureFacePairClosestPointResult {
    /** 面 A 上的最近点。 */
    pointOnA: THREE.Vector3;
    /** 面 B 上的最近点。 */
    pointOnB: THREE.Vector3;
}

/**
 * 面↔面 BVH 查询可选参数。
 */
export interface FeatureFacePairClosestQueryOptions {
    /** 最大搜索距离，用于 BVH 剪枝。 */
    maxThreshold?: number;
}

/** 复用 B→A 变换矩阵。 */
const geometryBToAMatrix = new THREE.Matrix4();

/** 复用 A 侧命中对象。 */
const hitOnA: FeatureFaceSpatialHitInfo = {
    point: new THREE.Vector3(),
    distance: 0,
    faceIndex: 0,
};

/** 复用 B 侧命中对象。 */
const hitOnB: FeatureFaceSpatialHitInfo = {
    point: new THREE.Vector3(),
    distance: 0,
    faceIndex: 0,
};

/**
 * 使用 BVH 求两特征面三角网格之间的最近点对。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @param options 可选剪枝参数。
 * @returns 最近点对；无法求解时返回 `null`。
 */
export function queryClosestPointsBetweenFeatureFaceMeshesViaBvh(
    faceA: FeatureFace,
    faceB: FeatureFace,
    options?: FeatureFacePairClosestQueryOptions,
): FeatureFacePairClosestPointResult | null {
    const spatialTreeA = FeatureFaceSpatialAccelerationEnsurer.ensure(faceA);
    if (!spatialTreeA?.closestPointToGeometry) {
        return null;
    }

    FeatureFaceSpatialAccelerationEnsurer.ensure(faceB);
    faceA.updateWorldMatrix(true, false);
    faceB.updateWorldMatrix(true, false);

    geometryBToAMatrix.copy(faceA.matrixWorld).invert().multiply(faceB.matrixWorld);

    const maxThreshold = options?.maxThreshold ?? Number.POSITIVE_INFINITY;
    const hit = spatialTreeA.closestPointToGeometry(
        faceB.geometry,
        geometryBToAMatrix,
        hitOnA,
        hitOnB,
        0,
        maxThreshold,
    );
    if (!hit) {
        return null;
    }

    return {
        pointOnA: hitOnA.point.clone().applyMatrix4(faceA.matrixWorld),
        pointOnB: hitOnB.point.clone().applyMatrix4(faceB.matrixWorld),
    };
}
